import { useAppDispatch, useAppSelector } from "@app/hooks";
import { selectPOIById } from "@features/survey/poisSelectors";
import { updateCategoryField } from "@features/survey/poisSlice";
import { selectCurrentSurveyId } from "@features/survey/surveySelectors";
import { useCurrentPoiId } from "@hooks/useSurveySelectors";
import { surveyPOIApi, useUpdatePOICategoryMutation } from "@services/survey-api";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";

interface UsePoiPointSizeReturn {
  pointSize: number;
  handleSliderChange: (newValue: number) => void;
}

/**
 * Manages POI category point size state and persistence.
 *
 * Shared between PoiIconSection and PoiLogoSection — both map pin types
 * honour the same `pointSize` field on the category.
 *
 * Handles:
 * - Local slider state (initialised from Redux on mount)
 * - Debounced optimistic Redux update
 * - Debounced optimistic RTK Query cache update
 * - Debounced API persistence via `updatePOICategory`
 */
export const usePoiPointSize = (): UsePoiPointSizeReturn => {
  const surveyId = useAppSelector(selectCurrentSurveyId);
  const dispatch = useAppDispatch();
  const currentPoiId = useCurrentPoiId();
  const currentPOI = useAppSelector((state) =>
    selectPOIById(currentPoiId)(state),
  );
  const categoryId = currentPOI?.categoryId || "";
  const [updatePOICategory] = useUpdatePOICategoryMutation();

  const [pointSize, setPointSize] = useState(
    currentPOI?.category?.pointSize || 28,
  );

  const debouncedUpdatePointSize = useMemo(
    () =>
      debounce((newValue: number) => {
        // 1. Optimistic Redux update
        dispatch(
          updateCategoryField({ categoryId, updates: { pointSize: newValue } }),
        );

        // 2. Optimistic RTK Query cache update
        dispatch(
          surveyPOIApi.util.updateQueryData(
            "getPOICategories",
            surveyId || "",
            (draft) => {
              const category = draft.find((c) => c.extId === categoryId);
              if (category) {
                category.iconData = category.iconData ?? {};
                category.pointSize = newValue;
              }
            },
          ),
        );

        // 3. Persist to backend
        updatePOICategory({
          surveyId: surveyId || "",
          categoryId,
          body: { pointSize: newValue },
        });
      }, 500),
    [categoryId, dispatch, surveyId, updatePOICategory],
  );

  useEffect(() => {
    return () => {
      debouncedUpdatePointSize.cancel();
    };
  }, [debouncedUpdatePointSize]);

  const handleSliderChange = (newValue: number) => {
    setPointSize(newValue);
    debouncedUpdatePointSize(newValue);
  };

  return { pointSize, handleSliderChange };
};
